import clsx from 'clsx';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    Disclosure,
    DisclosureChevron,
    DisclosurePanel,
    DisclosureTrigger,
    intlPluralizeString,
    Pressable,
    renderIcon,
    Tooltip,
} from '@gitbook/spine-react';
import { hasChangeRequestMergeEvent } from '@services/content-agents/browser';

import { NavigationLink, useNavigation } from '../../navigation';
import { isChangeRequestRunTask } from '../Agent';
import { DIFF_ICONS, DIFF_TEXT_COLORS, type DiffChangeType } from '../Diff';
import { computeChanges, type PageChange } from './changes';
import { type AgentEditTarget, getEditTargetNavigationTarget } from './editTarget';
import { EditTargetHeader, EditTargetTray } from './EditTargetTray';
import { useEditTargetDetails } from './useEditTargetDetails';

/**
 * Display an overview on top of the message input to summarize what the agent did, in the change
 * request — or the space edited live — the conversation is working in.
 *
 * Only that target's pages: a conversation can be pointed at another change request part-way
 * through, and the pages it edited before that belong to somewhere the user is no longer looking.
 */
export function AgentContentChangesOverview(props: {
    messages: api.AIMessage[];
    loading: boolean;
    /** Where the agent edits. Null until it has attached something, or from an older agent. */
    editTarget: AgentEditTarget | null;
    /**
     * Whether the conversation can only be read. The tray then also says what it is — nothing else
     * does, now that the composer is gone and the per-message task banner with it — and closes off
     * the conversation instead of tucking behind a composer that is not there.
     */
    consultOnly?: boolean;
    /**
     * What the surface offers as context of the target with the next message — the page being
     * read, the text selected in it — shown under the header, where the tray the conversation
     * started with showed them. Only with a target: the context is of that target, and the header
     * is what names it.
     */
    context?: React.ReactNode;
}) {
    const { messages, loading, editTarget, consultOnly = false, context } = props;

    const { changes, counts } = React.useMemo(
        () => computeChanges(messages, editTarget),
        [messages, editTarget]
    );
    // The run the conversation was opened for, if it was opened for one rather than by someone
    // typing. The message that carries it is the whole conversation, so this is where it is named.
    const run = React.useMemo(
        () =>
            messages.find((message) => message.task && isChangeRequestRunTask(message.task))?.task,
        [messages]
    );
    // What the agent is working on at this moment, if anything. A task rides on the message being
    // answered, which is the last one — so a reply typed after a run has landed carries none, and
    // is not that run still going.
    const activeTask = loading ? messages[messages.length - 1]?.task : undefined;
    const details = useEditTargetDetails(editTarget);
    const mergeShown =
        editTarget?.kind === 'change_request' &&
        hasChangeRequestMergeEvent(messages, String(editTarget.changeRequest));

    // Nothing attached and nothing edited: the conversation has not touched content at all.
    if (!editTarget && changes.length === 0) {
        return null;
    }

    const hasChanges = changes.length > 0;

    const header = (
        <EditTargetHeader
            {...(details ? { icon: details.icon } : {})}
            label={
                details
                    ? details.label
                    : `${loading ? 'Editing' : 'Edited'} ${changes.length} ${intlPluralizeString({
                          count: changes.length,
                          singular: 'page',
                          plural: 'pages',
                      })}`
            }
            {...(details?.number !== undefined ? { number: details.number } : {})}
        >
            {hasChanges ? (
                <div className="flex shrink-0 gap-1 text-ui-small">
                    {(
                        [
                            ['added', '+'],
                            ['modified', '+'],
                            ['deleted', '-'],
                        ] satisfies [DiffChangeType, string][]
                    ).map(([type, icon]) =>
                        counts[type] > 0 ? (
                            <Tooltip
                                key={type}
                                label={`${counts[type]} ${intlPluralizeString({
                                    count: counts[type],
                                    singular: 'page',
                                    plural: 'pages',
                                })} ${type}`}
                            >
                                <span className={DIFF_TEXT_COLORS[type]}>
                                    {icon}
                                    {counts[type]}
                                </span>
                            </Tooltip>
                        ) : null
                    )}
                </div>
            ) : null}
            {/* Tighter than the row's own spacing: these are controls of the header rather than
                more of what it reports, so they read as a pair set off from the counts. */}
            <div className="flex shrink-0 items-center gap-1">
                {hasChanges ? <DisclosureChevron className="size-3 text-neutral-low" /> : null}
                {editTarget && details ? (
                    // A link rather than a button, and in the header rather than in the panel: the
                    // target is worth opening whether or not the user cares which pages changed —
                    // and when nothing has been edited there is no panel to put it in. An anchor
                    // keeps middle-click and copy-link working, and its press stops at the link, so
                    // it never also toggles the panel it sits in.
                    <NavigationLink
                        to={getEditTargetNavigationTarget(editTarget)}
                        title={`Open ${details.noun}`}
                        aria-label={`Open ${details.noun}`}
                        className="flex size-4 items-center justify-center rounded-xs text-neutral-high opacity-60 transition-opacity hover:opacity-100"
                    >
                        <Icon.ArrowUpRight className="size-3.5" />
                    </NavigationLink>
                ) : null}
            </div>
        </EditTargetHeader>
    );

    return (
        <EditTargetTray animateIn hidden={mergeShown} standalone={consultOnly}>
            {/* Header and panel in one flex child: the tray spaces its children, and a collapsed
                panel would still take that gap and leave the header taller than the tray the
                conversation started with. */}
            <div className="flex flex-col">
                {hasChanges ? (
                    <Disclosure>
                        <DisclosureTrigger>
                            <Pressable>
                                <div className="cursor-pointer">{header}</div>
                            </Pressable>
                        </DisclosureTrigger>
                        <DisclosurePanel>
                            <div className="flex max-h-44 flex-col overflow-y-auto pt-1.5">
                                {changes.map((change) => (
                                    <PageChangeItem
                                        key={change.id}
                                        change={change}
                                        editTarget={editTarget}
                                    />
                                ))}
                            </div>
                        </DisclosurePanel>
                    </Disclosure>
                ) : (
                    // Nothing edited yet: the target is still worth naming, so the user can see what
                    // the conversation is attached to, but there is no list to open.
                    header
                )}
            </div>
            {editTarget ? context : null}
            {consultOnly ? (
                <div className="pl-2 text-ui-small text-neutral-low">
                    Review of this change request. Read-only.
                </div>
            ) : run?.type === 'implement_change_request' ? (
                // Where the conversation came from, which nothing else in it says: the run was
                // asked for on the change request rather than started by someone typing here —
                // the prompt it answers is not even in the transcript — and it goes on working
                // after the window it is watched from is closed.
                <div className="pl-2 text-ui-small text-neutral-low">
                    GitBook Agent was asked to implement this change request.
                    {activeTask?.type === 'implement_change_request'
                        ? ' It keeps working in the background.'
                        : null}
                </div>
            ) : null}
        </EditTargetTray>
    );
}

function PageChangeItem(props: { change: PageChange; editTarget: AgentEditTarget | null }) {
    const { change, editTarget } = props;
    const { page, baseRevisionId } = change;
    const { navigate } = useNavigation();

    return (
        <Pressable
            onPress={() => {
                navigate(
                    getEditTargetNavigationTarget(
                        // An agent that reports no target still edited somewhere: the space the
                        // change names, which is as much as it says.
                        editTarget ?? { kind: 'space', spaceId: change.spaceId },
                        { pagePath: page.path, baseRevisionId }
                    )
                );
            }}
        >
            <div className="flex cursor-pointer flex-row items-center gap-2 rounded-sm px-1.5 py-1 hover:bg-base">
                <div className="flex shrink-0">
                    {renderIcon(DIFF_ICONS[change.change], {
                        className: clsx(DIFF_TEXT_COLORS[change.change], 'size-3'),
                    })}
                </div>
                <div className="truncate text-ui-small text-base">{page.title}</div>
            </div>
        </Pressable>
    );
}
